import type { DoOrDumpDashboardPayload } from "@/types/do-or-dump-dashboard";

export type InsightShade = "green" | "yellow" | "orange" | "red";

/** Rating → value text color (tiles stay neutral grey; avoids tinted “cardboard” panels). */
export function insightRatingTextClasses(shade: InsightShade): string {
  switch (shade) {
    case "green":
      return "text-emerald-800";
    case "yellow":
      return "text-amber-900";
    case "orange":
      return "text-orange-900";
    case "red":
      return "text-rose-900";
    default:
      return "text-zinc-900";
  }
}

export function momentumShade(momentum: number): InsightShade {
  if (momentum >= 72) return "green";
  if (momentum >= 55) return "yellow";
  if (momentum >= 38) return "orange";
  return "red";
}

export function volumeGoalVsCeilingShade(
  unitsForGoal: number | null,
  capacity: number,
  profitPerItem: number,
): InsightShade {
  if (profitPerItem <= 0 || unitsForGoal == null) return "red";
  if (capacity <= 0) return "red";
  const ratio = unitsForGoal / capacity;
  if (ratio <= 1.05) return "green";
  if (ratio <= 1.3) return "yellow";
  if (ratio <= 1.75) return "orange";
  return "red";
}

export function timeTierShade(tier: string): InsightShade {
  const t = tier.toLowerCase();
  if (t === "strong") return "green";
  if (t === "medium") return "yellow";
  return "orange";
}

export function audienceTierShade(audienceTier: string): InsightShade {
  const t = audienceTier.toLowerCase();
  if (t === "strong") return "green";
  if (t === "medium") return "yellow";
  return "red";
}

/**
 * Tile shows only the model’s monthly unit ceiling (capacity), e.g. ~111 units/month.
 * Color still reflects goal-vs-ceiling fit via `volumeGoalVsCeilingShade` on the tile.
 */
export function unitsCeilingTileValue(payload: Pick<DoOrDumpDashboardPayload, "capacity">): string {
  return `~${payload.capacity.toLocaleString()} units/month`;
}
